import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { appStatusActions } from "../slices/appStatus";
import { schoolActions } from "../slices/school";
import { getErrorMessage } from "../../utils/errorUtils";
import { InstitutionSetupRequestDto } from "../../shared/dtos/institution/InstitutionSetupRequestDto";
import { SchoolResponseDto } from "../../shared/dtos/school/SchoolResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { AppStartupState } from "../../shared/enums/AppStartupState";

// 1. Initial System Setup Verification
export const checkSystemStatus =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(appStatusActions.setLoadingSetupCheck(true));
    dispatch(
      appStatusActions.setStartupState({
        state: AppStartupState.LOADING,
        message: "Verifying system installation...",
      }),
    );

    try {
      const { data } = await axiosInstance.get<ApiResponseDto<boolean>>(
        "/public/system-status",
        { headers: { requiresAuth: false } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        const isComplete = Boolean(data.data);
        dispatch(appStatusActions.setSetupComplete(isComplete));
      } else {
        dispatch(
          appStatusActions.setStartupState({
            state: AppStartupState.ERROR,
            message: data.message || "Failed to load system status.",
          }),
        );
      }
    } catch (error) {
      dispatch(
        appStatusActions.setStartupState({
          state: AppStartupState.ERROR,
          message: getErrorMessage(error),
        }),
      );
    } finally {
      dispatch(appStatusActions.setLoadingSetupCheck(false));
    }
  };

// 2. Initial Institution Setup Submission
export const setupInstitution =
  (payload: InstitutionSetupRequestDto) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    dispatch(appStatusActions.setLoadingInstitutionSetup());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<SchoolResponseDto>
      >("/public/setup-institution", payload, {
        headers: { requiresAuth: false },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Institution configured successfully!");
        dispatch(schoolActions.setSchool(data.data));
        dispatch(appStatusActions.institutionSetupSuccess());
        dispatch(appStatusActions.setSetupComplete(true));
        return true;
      }
      return false;
    } catch (error) {
      dispatch(
        appStatusActions.setErrorSetupInstitution(getErrorMessage(error)),
      );
      return false;
    }
  };

// 3. Re-validate License
export const revalidateLicense =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    dispatch(appStatusActions.setLoadingLicenseRevalidate(true));
    try {
      const { data } = await axiosInstance.get<ApiResponseDto<void>>(
        "/licenses/re-validate",
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "License re-validated successfully!");
        dispatch(appStatusActions.closeLicenseModal());
        setTimeout(() => {
          window.location.reload();
        }, 800);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      dispatch(appStatusActions.setLoadingLicenseRevalidate(false));
    }
  };
